import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { defaultTooltipConfig, defaultGridConfig, defaultLegendConfig, defaultScaleTitleConfig } from '../../utils/chartConfig';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const COLOURS = {
  portfolio: '#305496',
  property: '#AA8090',
  annuity: '#91CF9B',
  nonFinancial: '#011E41',
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const HouseholdWealthChart = React.forwardRef(({
  portfolioBalance,
  investmentProperty,
  annuityValue,
  nonFinancialAssets,
  years,
  age1,
}, ref) => {
  const labels = years.map((y) => `${y}`);

  const makeSeries = (label, arr, colour) => ({
    label,
    data: arr || years.map(() => 0),
    borderColor: colour,
    backgroundColor: colour + 'CC',
    borderWidth: 1,
    pointRadius: 0,
    pointHoverRadius: 4,
    tension: 0.3,
    fill: true,
    stack: 'wealth',
  });

  const data = {
    labels,
    datasets: [
      makeSeries('Non-Financial Assets', nonFinancialAssets, COLOURS.nonFinancial),
      makeSeries('Investment Property', investmentProperty, COLOURS.property),
      makeSeries('Annuity Value', annuityValue, COLOURS.annuity),
      makeSeries('Portfolio Balance', portfolioBalance, COLOURS.portfolio),
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: 'Household Wealth Over Time',
        font: { size: 14 },
      },
      legend: defaultLegendConfig,
      tooltip: {
        ...defaultTooltipConfig,
        callbacks: {
          title: (items) => {
            const idx = items[0]?.dataIndex;
            const year = years[idx];
            const age = age1 != null ? age1 + year : null;
            return age != null ? `Year ${year} (Age ${age})` : `Year ${year}`;
          },
          label: (item) => {
            return `${item.dataset.label}: ${formatCurrency(item.raw)}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Age',
        },
        ticks: {
          callback: function (value, index) {
            const year = years[index];
            if (age1 != null) {
              return `${age1 + year}`;
            }
            return `${year}`;
          },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Total Wealth ($)',
        },
        ticks: {
          callback: (value) => formatCurrency(value),
        },
      },
    },
  };

  return (
    <div style={{ height: '350px', position: 'relative' }}>
      <Line ref={ref} data={data} options={options} />
    </div>
  );
});

HouseholdWealthChart.displayName = 'HouseholdWealthChart';

export default HouseholdWealthChart;
